import {getTestProps} from '@benchling/js-test-utils';
import {
  Alert,
  Button,
  createStyles,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as net from 'util/Net';

import {
  CONTACT_US_MAX_NOTE_LENGTH,
  CONTACT_US_TABS,
  DEFAULT_CONTACT_US_TAB,
  getContactUsTab,
  type ContactUsFeedbackType,
} from './contactUsConfig';
import {submitModelHubFeedback} from './ContactUsService';

const TEST_COMPONENT = 'ContactUsModal';

export interface ContactUsModalProps {
  opened: boolean;
  onClose: () => void;
}

export default function ContactUsModal({opened, onClose}: ContactUsModalProps): JSX.Element {
  const {classes} = useStyles();
  const [feedbackType, setFeedbackType] = useState<ContactUsFeedbackType>(DEFAULT_CONTACT_US_TAB.value);
  const [note, setNote] = useState(DEFAULT_CONTACT_US_TAB.prefilledNote);
  // Tracks whether the note has been edited, so switching tabs only reseeds untouched text.
  const [noteDirty, setNoteDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) {
      return;
    }
    setFeedbackType(DEFAULT_CONTACT_US_TAB.value);
    setNote(DEFAULT_CONTACT_US_TAB.prefilledNote);
    setNoteDirty(false);
    setSubmitting(false);
    setError(null);
    setSuccessMessage(null);
  }, [opened]);

  const activeTab = getContactUsTab(feedbackType);

  const handleChangeFeedbackType = (value: string): void => {
    const nextType = value as ContactUsFeedbackType;
    setFeedbackType(nextType);
    setError(null);
    setSuccessMessage(null);
    // Never overwrite text the user typed; only reseed the prefill while the note is untouched.
    if (!noteDirty) {
      setNote(getContactUsTab(nextType).prefilledNote);
    }
  };

  const handleSubmit = (): void => {
    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);
    submitModelHubFeedback(feedbackType, note.trim())
      .then(() => {
        trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.CONTACT_US_SUBMITTED, {feedbackType});
        setSuccessMessage('Thanks! Your message was sent to the Benchling product team.');
        setNote('');
        setNoteDirty(false);
        setSubmitting(false);
      })
      .catch((e: unknown) => {
        setError(net.getUserMessage(e) ?? 'We couldn’t send your message. Please try again.');
        setSubmitting(false);
      });
  };

  return (
    <Modal
      classNames={{title: classes.modalTitle}}
      onClose={onClose}
      opened={opened}
      size="lg"
      title="Get in touch"
    >
      <Stack spacing="lg" {...getTestProps({component: TEST_COMPONENT, element: 'root'})}>
        <Stack spacing="xs">
          <Text className={classes.fieldLabel}>What kind of feedback is this?</Text>
          <Group>
            <SegmentedControl
              data={CONTACT_US_TABS.map((tab) => ({
                value: tab.value,
                label: (
                  <Group noWrap={true} spacing={7}>
                    <BenchlingIcon icon={tab.icon} />
                    {tab.segmentLabel}
                  </Group>
                ),
              }))}
              onChange={handleChangeFeedbackType}
              size="lg"
              value={feedbackType}
              variant="contained"
            />
          </Group>
        </Stack>

        <Textarea
          autosize={true}
          label={activeTab.noteLabel}
          maxLength={CONTACT_US_MAX_NOTE_LENGTH}
          minRows={5}
          onChange={(event) => {
            setNote(event.currentTarget.value);
            setNoteDirty(true);
          }}
          placeholder={activeTab.notePlaceholder}
          size="lg"
          value={note}
          withAsterisk={true}
          {...getTestProps({component: TEST_COMPONENT, element: 'note'})}
        />

        {successMessage != null && (
          <Alert color="green" variant="light">
            <Text size="lg">{successMessage}</Text>
          </Alert>
        )}
        {error != null && (
          <Alert color="red" variant="light">
            <Text size="lg">{error}</Text>
          </Alert>
        )}

        <Group position="apart" spacing="md">
          <Text color="dimmed" size="md">
            Shared with the Benchling product team
          </Text>
          <Button disabled={note.trim() === ''} loading={submitting} onClick={handleSubmit} size="lg">
            Send
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const useStyles = createStyles((theme) => ({
  fieldLabel: {
    color: theme.colors.gray[9],
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
  },
  modalTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: 600,
  },
}));
